import { Fragment, type ReactNode } from "react";
import type { BoardColumnDef } from "./Board.types";
import type { BoardColumnAddingResolver } from "./boardAdd";
import { getCellEntries, type BoardCells } from "./boardCells";
import { BoardHeaderAdd } from "./BoardCellList";
import { getExpandedColumns, type BoardCollapse } from "./boardCollapse";
import {
    BoardCollapsedColumnHeader,
    BoardColumnToggle,
    type BoardCollapsedColumnProps,
} from "./BoardCollapsedColumn";
import { BoardColumnHeader } from "./BoardColumnHeader";

export interface BoardColumnRefusal {
    reason: string;
    /** The dragged card is over the column: the reason shows unprompted. */
    isShown: boolean;
}

export interface BoardStripRenderProps extends BoardCollapsedColumnProps {
    column: BoardColumnDef;
}

/** Collapsed strips before the first expanded column, which lane titles clear. */
export const countLeadingStrips = (
    columns: readonly BoardColumnDef[],
    collapse: BoardCollapse,
) => getExpandedColumns(columns, collapse)[0]?.columnIndex ?? columns.length;

interface UseBoardColumnChromeArgs<TItem> {
    cells: BoardCells<TItem>;
    collapse: BoardCollapse;
    hasLanes: boolean;
    getColumnTitleId: (index: number) => string;
    getColumnRefusal?: (columnId: string) => BoardColumnRefusal | undefined;
    /** Also gives the header `+`, shown only on boards without lanes. */
    getColumnAdding: BoardColumnAddingResolver<TItem>;
    renderCollapsedColumn: (props: BoardStripRenderProps) => ReactNode;
}

/**
 * What `BoardLayout` draws around each column's cards: its header (title,
 * count, refusal, header `+`, collapse toggle), or its collapsed strip. With
 * lanes a collapsed column's header holds just its expand toggle, so the
 * strip starts under the header row's line.
 */
export const useBoardColumnChrome = <TItem,>({
    cells,
    collapse,
    hasLanes,
    getColumnTitleId,
    getColumnRefusal,
    getColumnAdding,
    renderCollapsedColumn,
}: UseBoardColumnChromeArgs<TItem>) => {
    const isCollapsed = (column: BoardColumnDef) =>
        collapse.columnIds.has(column.id);
    const renderHeaderAdd = (column: BoardColumnDef) => {
        const adding = getColumnAdding(column, null);
        const placement = { columnId: column.id, laneId: null };
        return (
            adding?.placements.has("header") && (
                <BoardHeaderAdd
                    adding={adding}
                    placement={placement}
                    entries={getCellEntries(cells, placement)}
                />
            )
        );
    };
    const getColumnToggle = (column: BoardColumnDef) => {
        const { onToggleColumn } = collapse;
        return (
            onToggleColumn && (
                <BoardColumnToggle
                    name={column.title}
                    isExpanded={!collapse.columnIds.has(column.id)}
                    onToggle={() => onToggleColumn(column.id)}
                />
            )
        );
    };
    const renderHeader = (column: BoardColumnDef, index: number) => {
        if (isCollapsed(column)) {
            return (
                <BoardCollapsedColumnHeader toggle={getColumnToggle(column)} />
            );
        }
        const refusal = getColumnRefusal?.(column.id);
        return (
            <BoardColumnHeader
                titleId={getColumnTitleId(index)}
                title={column.title}
                icon={column.icon}
                count={cells.columnCounts.get(column.id) ?? 0}
                totalCount={column.totalCount}
                actions={column.actions}
                refusalReason={refusal?.reason}
                isRefusalShown={refusal?.isShown ?? false}
                addButton={hasLanes ? null : renderHeaderAdd(column)}
                collapseToggle={getColumnToggle(column)}
            />
        );
    };
    const renderStrip = (column: BoardColumnDef, index: number) => (
        <Fragment key={column.id}>
            {renderCollapsedColumn({
                column,
                title: column.title,
                titleId: getColumnTitleId(index),
                count: cells.columnCounts.get(column.id) ?? 0,
                toggle: hasLanes ? null : getColumnToggle(column),
                style: {
                    gridColumn: index + 1,
                    gridRow: hasLanes ? 2 : undefined,
                },
            })}
        </Fragment>
    );
    return { renderHeader, renderStrip, isCollapsed };
};
